import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { FilterBarProps } from 'src/types/analytics';

const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  onFiltersChange,
  onReset,
  availableChannels = [],
  availableStatuses = [],
  loading = false
}) => {
  // Keep track of input values separately from filters
  const [dateInputs, setDateInputs] = useState({
    startDate: filters.startDate,
    endDate: filters.endDate
  });

  const isValidDate = (dateStr: string): boolean => {
    // Check basic format YYYY-MM-DD
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;

    // Parse the date parts
    const [year, month, day] = dateStr.split('-').map(Number);

    // Validate reasonable year range (e.g., between 2000 and 2100)
    if (year < 2000 || year > 2100) return false;

    // Validate month (1-12)
    if (month < 1 || month > 12) return false;

    // Validate day based on month
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > daysInMonth) return false;

    return true;
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Always update the input value
    setDateInputs(prev => ({
      ...prev,
      [name]: value
    }));

    // Only trigger filter change if date is valid or if explicitly clearing the field
    if (value === '') {
      onFiltersChange({ [name]: null }); // Use null instead of empty string when clearing
    } else if (isValidDate(value)) {
      onFiltersChange({ [name]: value });
    }
  };

  const handleDateFocus = (name: string) => {
    // Clear the input when focused
    setDateInputs(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  const handleGroupByChange = (
    _: React.MouseEvent<HTMLElement>,
    value: 'day' | 'week' | 'month' | null
  ) => {
    if (value) {
      onFiltersChange({ groupBy: value });
    }
  };

  const handleChannelsChange = (event) => {
    onFiltersChange({ channels: event.target.value as string[] });
  };

  const handleStatusChange = (event) => {
    onFiltersChange({ status: event.target.value as string[] });
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1,
        mb: 3,
        backgroundColor: 'background.paper',
        boxShadow: 1
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        mb={2}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems="center"
          flexGrow={1}
        >
          <TextField
            label="Start Date"
            type="date"
            name="startDate"
            value={dateInputs.startDate || ''}
            onChange={handleDateChange}
            onFocus={() => handleDateFocus('startDate')}
            onBlur={() => {
              // On blur, if date is invalid, revert to last valid date
              if (!isValidDate(dateInputs.startDate) && dateInputs.startDate !== '') {
                setDateInputs(prev => ({
                  ...prev,
                  startDate: filters.startDate || ''
                }));
              }
            }}
            InputLabelProps={{ shrink: true }}
            disabled={loading}
            fullWidth
            size="small"
          />
          <TextField
            label="End Date"
            type="date"
            name="endDate"
            value={dateInputs.endDate || ''}
            onChange={handleDateChange}
            onFocus={() => handleDateFocus('endDate')}
            onBlur={() => {
              // On blur, if date is invalid, revert to last valid date
              if (!isValidDate(dateInputs.endDate) && dateInputs.endDate !== '') {
                setDateInputs(prev => ({
                  ...prev,
                  endDate: filters.endDate || ''
                }));
              }
            }}
            InputLabelProps={{ shrink: true }}
            disabled={loading}
            fullWidth
            size="small"
          />
        </Stack>

        <ToggleButtonGroup
          value={filters.groupBy}
          exclusive
          onChange={handleGroupByChange}
          aria-label="time grouping"
          disabled={loading}
          size="small"
        >
          <ToggleButton value="day" aria-label="daily">
            Daily
          </ToggleButton>
          <ToggleButton value="week" aria-label="weekly">
            Weekly
          </ToggleButton>
          <ToggleButton value="month" aria-label="monthly">
            Monthly
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems="center"
          width="100%"
        >
          <FormControl fullWidth size="small">
            <InputLabel>Channels</InputLabel>
            <Select
              multiple
              value={filters.channels || []}
              onChange={handleChannelsChange}
              label="Channels"
              disabled={loading}
            >
              {availableChannels.map((channel) => (
                <MenuItem key={channel.id} value={channel.id}>
                  {channel.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={filters.status || []}
              onChange={handleStatusChange}
              label="Status"
              disabled={loading}
            >
              {availableStatuses.map((status) => (
                <MenuItem key={status.id} value={status.id}>
                  {status.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            onClick={onReset}
            disabled={loading}
          >
            Reset
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default FilterBar; 